import { Skeleton } from "@mantine/core";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { useListBrands } from "../../../api/generated/endpoints/teams/teams";
import type { Brand } from "../../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../../api/signed-urls";
import { DeleteBrand } from "../../../platform/brands/DeleteBrand";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { useMembership } from "../../../platform/membership";
import { managesBrands } from "../../../platform/permissions";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import home from "../home2.module.css";
import library from "../library/library.module.css";
import classes from "./brands.module.css";

/** The prototype's coloured square: the brand's logo, else its first colour, else its initial. */
function Mark({ brand }: { brand: Brand }) {
  const color = brand.colors[0]?.hex;
  if (brand.logo_url !== null) {
    return <img className={classes.mark} src={brand.logo_url} alt="" loading="lazy" decoding="async" />;
  }
  return (
    <span
      className={classes.mark}
      // The brand's own colour, from its data.
      style={color === undefined ? undefined : { background: color }}
      aria-hidden
    >
      {color === undefined ? brand.name.slice(0, 1).toUpperCase() : null}
    </span>
  );
}

/**
 * The prototype's `#brands`: the team's brands on the Brands page's own read, the current one
 * marked, each switched to on this page; Edit and Add brand open the current forms, and Delete is
 * the Brands page's own (spec ui-perf/pro-studio-redesign, PR d5, decision 32).
 */
export function BrandsPage() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/home2/brands",
  });
  const navigate = useNavigate();
  const me = useMembership(teamId);
  const canManage = me !== undefined && managesBrands(me.role);
  // The logos' signed URLs expire: the list is read again before they do, as the Brands page's.
  const brands = useListBrands(teamId, {
    query: freshUrls((list: Brand[]) => soonestExpiry(list.map((each) => each.urls_expire_at))),
  });
  const [deleting, setDeleting] = useState<Brand | undefined>(undefined);
  const list = brands.data;
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Brands</h1>
          <p className={page.lede}>
            Logo, colors and fonts used in your ads. Switch brands from the top bar.
          </p>
        </div>
        {canManage ? (
          <Link
            to="/teams/$teamId/brands/$brandId/home2/brands/new"
            params={{ teamId, brandId }}
            className={library.primary}
          >
            Add brand
          </Link>
        ) : null}
      </div>
      {brands.isError ? <ErrorAlert error={brands.error} /> : null}
      {list === undefined ? (
        <div className={classes.grid}>
          {[0, 1, 2].map((each) => (
            <Skeleton key={each} className={classes.skeleton} />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className={create.empty}>
          <b>No brands yet</b>
          <span className={home.small}>Add a brand and every tool can use it.</span>
        </div>
      ) : (
        <ul className={classes.grid} aria-label="Brands">
          {list.map((brand) => {
            const current = brand.id.toLowerCase() === brandId.toLowerCase();
            return (
              <li key={brand.id}>
                <article
                  className={classes.card}
                  aria-label={brand.name}
                  aria-current={current ? "true" : undefined}
                >
                  <div className={classes.top}>
                    <Mark brand={brand} />
                    <div className={classes.titles}>
                      {/* The brand's page, under that brand, as Classic's Brands list opens it. */}
                      <Link
                        to="/teams/$teamId/brands/$brandId/home2/brand"
                        params={{ teamId, brandId: brand.id }}
                        className={classes.name}
                        title={brand.name}
                      >
                        {brand.name}
                      </Link>
                      {brand.website === null ? null : (
                        <span className={classes.site} title={brand.website}>
                          {brand.website}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={classes.actions}>
                    {current ? (
                      <span className={classes.current}>Current brand</span>
                    ) : (
                      <Link
                        to="/teams/$teamId/brands/$brandId/home2/brands"
                        params={{ teamId, brandId: brand.id }}
                        className={classes.switch}
                      >
                        Switch to
                      </Link>
                    )}
                    <span className={create.grow} />
                    {canManage ? (
                      <>
                        <Link
                          to="/teams/$teamId/brands/$brandId/home2/brand/edit"
                          params={{ teamId, brandId: brand.id }}
                          className={classes.text}
                          aria-label={`Edit ${brand.name}`}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className={classes.text}
                          aria-label={`Delete ${brand.name}`}
                          onClick={() => setDeleting(brand)}
                        >
                          Delete
                        </button>
                      </>
                    ) : null}
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}
      {deleting === undefined ? null : (
        <DeleteBrand
          teamId={teamId}
          brand={deleting}
          onCancel={() => setDeleting(undefined)}
          // The current brand gone, the team's way in opens its first brand left; another, this page.
          onDeleted={async () => {
            const gone = deleting.id.toLowerCase() === brandId.toLowerCase();
            setDeleting(undefined);
            if (gone) await navigate({ to: "/teams/$teamId/home2", params: { teamId }, replace: true });
          }}
        />
      )}
    </div>
  );
}
